import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import AddTeamsFormDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/AddTeamsFormDialog';
import AddOrgFormDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/AddOrgFormDialog';
import AddOrgRefFormDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/AddOrgRefFormDialog';
import RemoveNodeConfirmDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/RemoveNodeConfirmDialog';
import MoveNodeConfirmDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/MoveNodeConfirmDialog';
import RenameNodeConfirmDialog from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/RenameNodeConfirmDialog';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import OrgTreeDragDrop from './OrgTreeDragDrop';
import { OrgTreeContext } from './OrgTreeContext';
import {
  treeStateReducer,
  getInitialTreeState,
  findItem,
} from './org-tree-state';
import { descendentsOfNode, pathToChild } from 'js/utils/org-utils';

function nearestIntermediateRootAncestorToNode(tree, node) {
  if (!tree || !node) {
    return undefined;
  }

  const roots = pathToChild(tree, node.id)
    ?.slice(1)
    ?.filter((n) => n.isRoot);

  return roots?.[roots.length - 1];
}

function intermediateRootsAffectedByMove(orgTree, pendingMove) {
  if (!orgTree || !pendingMove) {
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const node of [pendingMove.node, pendingMove.targetNode]) {
    if (!node) {
      continue;
    }
    const r = nearestIntermediateRootAncestorToNode(orgTree, node);
    if (r && !seen.has(r.id)) {
      seen.add(r.id);
      out.push(r);
    }
  }
  return out;
}

function OrgTreeView({
  orgTree,
  teams,
  rootOrgs,
  onAddTeamsToOrg,
  onAddGroup,
  onAddOrgRef,
  onRemoveNode,
  onRename,
  onPromoteToOrg,
  onReparentGroup,
  onMoveTeam,
}) {
  const [state, updateState] = useReducer(
    treeStateReducer,
    orgTree,
    getInitialTreeState
  );

  useEffect(() => {
    updateState({ type: 'refresh', orgTree });
  }, [orgTree]);

  const uniqueContextId = useMemo(
    () => Symbol(`org-tree-${orgTree?.id}`),
    [orgTree?.id]
  );

  const findTreeItem = useCallback(
    (itemId) => findItem(state, itemId),
    [state]
  );

  const [activeDropHighlightKey, setActiveDropHighlightKey] = useState(null);
  const [isOrgTreeDragActive, setIsOrgTreeDragActive] = useState(false);

  const orgTreeContextValue = useMemo(
    () => ({
      dispatch: updateState,
      uniqueContextId,
      find: findTreeItem,
      activeDropHighlightKey,
      setActiveDropHighlightKey,
      setIsOrgTreeDragActive,
    }),
    [updateState, findTreeItem, uniqueContextId, activeDropHighlightKey]
  );

  const handleExpand = useCallback(
    (node, cascade = false) => {
      const ids = cascade
        ? [
            node.depthId,
            ...descendentsOfNode(node)
              .filter((n) => n.type === 'org')
              .map((n) => n.depthId),
          ]
        : [node.depthId];
      ids.forEach((id) => updateState({ type: 'expand', itemId: id }));
    },
    [updateState]
  );

  const handleCollapse = useCallback(
    (node, cascade = false) => {
      const ids = cascade
        ? [
            node.depthId,
            ...descendentsOfNode(node)
              .filter((n) => n.type === 'org')
              .map((n) => n.depthId),
          ]
        : [node.depthId];
      ids.forEach((id) => updateState({ type: 'collapse', itemId: id }));
    },
    [updateState]
  );

  /** At most one modal at a time; drives add/remove/rename/move/promote flows. */
  const [modificationDialog, setModificationDialog] = useState(null);

  const renameDeferredRef = useRef(null);

  const intermediateRootForModification = useMemo(() => {
    if (!orgTree || !modificationDialog) {
      return undefined;
    }
    const { kind } = modificationDialog;
    if (kind === 'addTeams' || kind === 'addGroup' || kind === 'addOrgRef') {
      return nearestIntermediateRootAncestorToNode(
        orgTree,
        modificationDialog.parentNode
      );
    }
    if (kind === 'remove') {
      return nearestIntermediateRootAncestorToNode(
        orgTree,
        modificationDialog.nodeToRemove
      );
    }
    if (kind === 'rename') {
      return nearestIntermediateRootAncestorToNode(
        orgTree,
        modificationDialog.node
      );
    }
    return undefined;
  }, [orgTree, modificationDialog]);

  const intermediateRootsForModification = useMemo(() => {
    if (!orgTree || modificationDialog?.kind !== 'move') {
      return [];
    }
    return intermediateRootsAffectedByMove(orgTree, modificationDialog.move);
  }, [orgTree, modificationDialog]);

  const closeModificationDialog = useCallback(() => {
    setModificationDialog(null);
  }, []);

  const handleAddTeams = useCallback((parentNode) => {
    setModificationDialog({ kind: 'addTeams', parentNode });
  }, []);

  const handleConfirmAddTeams = useCallback(
    (teamIds) => {
      const ctx = modificationDialog;
      if (ctx?.kind !== 'addTeams') {
        return Promise.resolve();
      }
      return onAddTeamsToOrg(ctx.parentNode, teamIds).then(() => {
        setModificationDialog(null);
        handleExpand(ctx.parentNode, false);
      });
    },
    [onAddTeamsToOrg, modificationDialog, handleExpand]
  );

  const handleAddGroup = useCallback((parentNode) => {
    setModificationDialog({ kind: 'addGroup', parentNode });
  }, []);

  const handleConfirmAddGroup = useCallback(
    (group) => {
      const ctx = modificationDialog;
      if (ctx?.kind !== 'addGroup') {
        return Promise.resolve();
      }
      return Promise.resolve(onAddGroup(ctx.parentNode, group)).then(() => {
        setModificationDialog(null);
        handleExpand(ctx.parentNode, false);
      });
    },
    [onAddGroup, modificationDialog, handleExpand]
  );

  const handleAddOrgRef = useCallback((parentNode) => {
    setModificationDialog({ kind: 'addOrgRef', parentNode });
  }, []);

  const handleConfirmAddOrgRef = useCallback(
    (orgId) => {
      const ctx = modificationDialog;
      if (ctx?.kind !== 'addOrgRef') {
        return Promise.resolve();
      }
      return Promise.resolve(onAddOrgRef(ctx.parentNode, orgId)).then(() => {
        setModificationDialog(null);
        handleExpand(ctx.parentNode, false);
      });
    },
    [onAddOrgRef, modificationDialog, handleExpand]
  );

  const handleRemove = useCallback((path, nodeToRemove) => {
    setModificationDialog({ kind: 'remove', path, nodeToRemove });
  }, []);

  const handleConfirmRemove = useCallback(() => {
    const ctx = modificationDialog;
    if (ctx?.kind !== 'remove') {
      return Promise.resolve();
    }
    return Promise.resolve(
      onRemoveNode(ctx.path[ctx.path.length - 1], ctx.nodeToRemove)
    ).then(() => setModificationDialog(null));
  }, [onRemoveNode, modificationDialog]);

  const handleRenameRequest = useCallback(
    (pathToNode, node, name) => {
      if (name === node.name) {
        return Promise.resolve();
      }
      const underNestedRef = Boolean(
        nearestIntermediateRootAncestorToNode(orgTree, node)
      );
      if (!underNestedRef) {
        return onRename(pathToNode, node, name);
      }
      return new Promise((resolve) => {
        renameDeferredRef.current = { resolve, pathToNode, node, name };
        setModificationDialog({ kind: 'rename', pathToNode, node, name });
      });
    },
    [orgTree, onRename]
  );

  const handleConfirmRenameNested = useCallback(() => {
    const d = renameDeferredRef.current;
    if (!d) {
      return Promise.resolve();
    }
    const { resolve, pathToNode, node, name } = d;
    return Promise.resolve(onRename(pathToNode, node, name)).finally(() => {
      renameDeferredRef.current = null;
      setModificationDialog(null);
      resolve();
    });
  }, [onRename]);

  const handleCancelRenameNested = useCallback(() => {
    const d = renameDeferredRef.current;
    if (d) {
      d.resolve();
      renameDeferredRef.current = null;
    }
    closeModificationDialog();
  }, [closeModificationDialog]);

  const handlePromoteToOrg = useCallback((node) => {
    setModificationDialog({ kind: 'promote', node });
  }, []);

  const handleConfirmPromoteToOrg = useCallback(() => {
    const ctx = modificationDialog;
    if (ctx?.kind !== 'promote') {
      return Promise.resolve();
    }
    return Promise.resolve(onPromoteToOrg(ctx.node)).finally(() =>
      setModificationDialog(null)
    );
  }, [onPromoteToOrg, modificationDialog]);

  const executeMove = useCallback(
    (move) => {
      if (move.type === 'group') {
        return onReparentGroup(move);
      }
      return onMoveTeam(move);
    },
    [onReparentGroup, onMoveTeam]
  );

  const handleReparentGroup = useCallback(
    (move) => {
      const sourceUnderNestedReferencedOrg = Boolean(
        nearestIntermediateRootAncestorToNode(orgTree, move.node)
      );
      const dropTargetInNestedReferencedOrg = Boolean(
        nearestIntermediateRootAncestorToNode(orgTree, move.targetNode)
      );
      const needsConfirm =
        sourceUnderNestedReferencedOrg ||
        dropTargetInNestedReferencedOrg ||
        move.node?.isRoot ||
        descendentsOfNode(move.node).some((n) => n.isRoot);

      if (needsConfirm) {
        setModificationDialog({
          kind: 'move',
          move: { ...move, type: 'group' },
        });
        return;
      }

      return executeMove({ ...move, type: 'group' });
    },
    [executeMove, orgTree]
  );

  const handleMoveTeam = useCallback(
    (move) => {
      const sourceUnderNestedReferencedOrg = Boolean(
        nearestIntermediateRootAncestorToNode(orgTree, move.node)
      );
      const targetUnderNestedReferencedOrg = Boolean(
        nearestIntermediateRootAncestorToNode(orgTree, move.targetNode)
      );
      const needsConfirm =
        sourceUnderNestedReferencedOrg || targetUnderNestedReferencedOrg;

      if (needsConfirm) {
        setModificationDialog({
          kind: 'move',
          move: { ...move, type: 'team' },
        });
        return;
      }

      return executeMove({ ...move, type: 'team' });
    },
    [executeMove, orgTree]
  );

  const handleConfirmMove = useCallback(() => {
    const ctx = modificationDialog;
    if (ctx?.kind !== 'move') {
      return Promise.resolve();
    }

    return Promise.resolve(executeMove(ctx.move)).finally(() => {
      setModificationDialog(null);
    });
  }, [modificationDialog, executeMove]);

  return (
    <div
      className={cx('org-mgmt-tree mb-3', {
        'org-mgmt-tree--drag-active': isOrgTreeDragActive,
      })}
    >
      <OrgTreeContext.Provider value={orgTreeContextValue}>
        <OrgTreeDragDrop
          orgTree={orgTree}
          onExpand={handleExpand}
          onCollapse={handleCollapse}
          onAddTeams={handleAddTeams}
          onAddGroup={handleAddGroup}
          onAddOrgRef={handleAddOrgRef}
          onRemove={handleRemove}
          onRename={handleRenameRequest}
          onPromoteToOrg={handlePromoteToOrg}
          onReparentGroup={handleReparentGroup}
          onMoveTeam={handleMoveTeam}
        />
      </OrgTreeContext.Provider>

      <AddTeamsFormDialog
        isOpen={modificationDialog?.kind === 'addTeams'}
        parentNode={modificationDialog?.parentNode}
        teams={teams}
        onCancel={closeModificationDialog}
        onConfirm={handleConfirmAddTeams}
        intermediateRoot={intermediateRootForModification}
      />

      <AddOrgFormDialog
        isOpen={modificationDialog?.kind === 'addGroup'}
        parentNode={modificationDialog?.parentNode}
        onCancel={closeModificationDialog}
        onConfirm={handleConfirmAddGroup}
        intermediateRoot={intermediateRootForModification}
      />

      <AddOrgRefFormDialog
        isOpen={modificationDialog?.kind === 'addOrgRef'}
        parentNode={modificationDialog?.parentNode}
        orgTree={orgTree}
        rootOrgs={rootOrgs}
        onCancel={closeModificationDialog}
        onConfirm={handleConfirmAddOrgRef}
        intermediateRoot={intermediateRootForModification}
      />

      <RemoveNodeConfirmDialog
        nodeToRemove={modificationDialog?.nodeToRemove}
        path={modificationDialog?.path}
        intermediateRoot={intermediateRootForModification}
        onConfirm={handleConfirmRemove}
        onCancel={closeModificationDialog}
        isOpen={modificationDialog?.kind === 'remove'}
      />

      <RenameNodeConfirmDialog
        node={modificationDialog?.node}
        newName={modificationDialog?.name}
        intermediateRoot={intermediateRootForModification}
        isOpen={modificationDialog?.kind === 'rename'}
        onConfirm={handleConfirmRenameNested}
        onCancel={handleCancelRenameNested}
      />

      <MoveNodeConfirmDialog
        nodeToMove={modificationDialog?.move?.node}
        targetNode={modificationDialog?.move?.targetNode}
        intermediateRoots={intermediateRootsForModification}
        onConfirm={handleConfirmMove}
        onCancel={closeModificationDialog}
        isOpen={modificationDialog?.kind === 'move'}
      />

      <ConfirmModal
        onCancel={closeModificationDialog}
        onConfirm={handleConfirmPromoteToOrg}
        isOpen={modificationDialog?.kind === 'promote'}
        confirmButtonLabel="Promote to Organization"
        title="Promote Group?"
      >
        <p>
          Confirm that you want to promote{' '}
          <strong>{modificationDialog?.node?.name}</strong> to an organization.
        </p>
        <p>
          Organizations are available directly from the organizations page and
          can be added to other organizations and groups.
        </p>
        <p>This action cannot be undone.</p>
      </ConfirmModal>
    </div>
  );
}

export default OrgTreeView;
